import { GAME } from "../config/gameConfig";
import { Board } from "./Board";
import { kickTests, nextRotation } from "./Rotation";
import { cellsFor, isSpanning, PieceBag } from "./Pieces";
import type { ActivePiece, GameEvent, GameMode, GamePhase, Layer, PieceType, Rotation } from "./types";

const LINE_SCORES = [0, 100, 300, 500, 800] as const;

export class Game {
  readonly boards: Record<Layer, Board> = { front: new Board(), back: new Board() };
  phase: GamePhase = "title";
  active: ActivePiece | null = null;
  nextType: PieceType = "TRI_BAR";
  score = 0;
  lines = 0;
  level = 1;

  private readonly bag = new PieceBag();
  private currentMode: GameMode = "standard";
  private events: GameEvent[] = [];
  // Layer names identify the cyan/red boards; their physical depth can swap.
  private foreground: Layer = "front";
  private gravityAccumulator = 0;
  private lockAccumulator = 0;
  private layerSwitchRemaining = 0;
  private playingMs = 0;

  get isLayerSwitching(): boolean { return this.layerSwitchRemaining > 0; }
  get layerSwitchProgress(): number { return 1 - this.layerSwitchRemaining / GAME.layerSwitchMs; }
  get foregroundLayer(): Layer { return this.foreground; }
  get mode(): GameMode { return this.currentMode; }
  get gravityMs(): number {
    return Math.max(GAME.minGravityMs, GAME.initialGravityMs
      * Math.pow(GAME.gravityFactor, this.level - 1)
      * Math.pow(GAME.timeGravityFactor, this.playingMs / GAME.timeGravityIntervalMs));
  }

  constructor() {
    this.nextType = this.bag.next();
  }

  start(mode: GameMode = "standard"): void {
    this.boards.front.reset();
    this.boards.back.reset();
    this.currentMode = mode;
    this.bag.reset(mode);
    this.nextType = this.bag.next();
    this.foreground = "front";
    this.score = 0;
    this.lines = 0;
    this.level = 1;
    this.gravityAccumulator = 0;
    this.lockAccumulator = 0;
    this.layerSwitchRemaining = 0;
    this.playingMs = 0;
    this.phase = "playing";
    this.events.push({ type: "start" });
    this.spawn();
  }

  update(deltaMs: number): void {
    if (this.phase !== "playing" || !this.active) return;
    let delta = Math.min(deltaMs, 100);
    if (this.layerSwitchRemaining > 0) {
      const frozen = Math.min(delta, this.layerSwitchRemaining);
      this.layerSwitchRemaining -= frozen;
      delta -= frozen;
      if (delta <= 0) return;
    }
    this.playingMs += delta;
    this.gravityAccumulator += delta;
    const gravity = this.gravityMs;

    while (this.gravityAccumulator >= gravity) {
      this.gravityAccumulator -= gravity;
      if (!this.tryMove(0, 1, false)) break;
    }

    if (this.isGrounded()) {
      this.lockAccumulator += delta;
      if (this.lockAccumulator >= GAME.lockDelayMs) this.lockActive();
    } else {
      this.lockAccumulator = 0;
    }
  }

  togglePause(): void {
    if (this.phase === "playing") {
      this.phase = "paused";
      this.events.push({ type: "pause", paused: true });
    } else if (this.phase === "paused") {
      this.phase = "playing";
      this.events.push({ type: "pause", paused: false });
    }
  }

  moveHorizontal(direction: -1 | 1): boolean {
    return this.tryMove(direction, 0, true);
  }

  softDrop(): boolean {
    if (this.phase !== "playing" || this.isLayerSwitching) return false;
    const fromY = this.active?.y ?? 0;
    const moved = this.tryMove(0, 1, false);
    if (moved) {
      this.score += 1;
      this.events.push({ type: "drop", hard: false, piece: { ...this.active! }, fromY });
    }
    return moved;
  }

  hardDrop(): void {
    if (this.phase !== "playing" || !this.active || this.isLayerSwitching) return;
    let distance = 0;
    const fromY = this.active.y;
    while (this.tryMove(0, 1, false)) distance += 1;
    this.score += distance * 2;
    this.events.push({ type: "drop", hard: true, piece: { ...this.active }, fromY });
    this.lockActive();
  }

  rotate(direction: -1 | 1): boolean {
    if (this.phase !== "playing" || !this.active || this.isLayerSwitching) return false;
    const piece = this.active;
    const targetRotation = nextRotation(piece.rotation, direction);
    for (const kick of kickTests(piece.type, piece.rotation, targetRotation)) {
      // SRS uses positive Y upward; board rows increase downward.
      const targetX = piece.x + kick.x;
      const targetY = piece.y - kick.y;
      if (this.canPlace(piece, targetX, targetY, targetRotation)) {
        piece.x = targetX;
        piece.y = targetY;
        piece.rotation = targetRotation;
        this.lockAccumulator = 0;
        this.events.push({ type: "rotate", piece: { ...piece } });
        return true;
      }
    }
    return false;
  }

  switchLayer(): boolean {
    if (this.phase !== "playing" || !this.active || this.isLayerSwitching) return false;
    const from = this.foreground;
    const to: Layer = from === "front" ? "back" : "front";
    if (!this.canPlace({ ...this.active, layer: to })) {
      this.events.push({ type: "layer-blocked", target: to });
      return false;
    }
    this.active.layer = to;
    this.foreground = to;
    this.layerSwitchRemaining = GAME.layerSwitchMs;
    this.lockAccumulator = 0;
    this.events.push({ type: "layer-switch", from, to, piece: { ...this.active } });
    return true;
  }

  landingY(): number | null {
    if (!this.active) return null;
    let y = this.active.y;
    while (this.canPlace(this.active, this.active.x, y + 1)) y += 1;
    return y;
  }

  drainEvents(): GameEvent[] {
    const result = this.events;
    this.events = [];
    return result;
  }

  private tryMove(dx: number, dy: number, resetLock: boolean): boolean {
    if (this.phase !== "playing" || !this.active || this.isLayerSwitching) return false;
    const x = this.active.x + dx;
    const y = this.active.y + dy;
    if (!this.canPlace(this.active, x, y)) return false;
    this.active.x = x;
    this.active.y = y;
    if (resetLock) this.lockAccumulator = 0;
    if (dx !== 0) this.events.push({ type: "move", piece: { ...this.active } });
    return true;
  }

  private isGrounded(): boolean {
    return Boolean(
      this.active &&
      !this.canPlace(this.active, this.active.x, this.active.y + 1),
    );
  }

  private lockActive(): void {
    if (!this.active) return;
    const piece = this.active;
    const layers: Layer[] = isSpanning(piece.type) ? ["front", "back"] : [piece.layer];
    // Lock both copies before either board clears and shifts its rows.
    let overflow = false;
    for (const layer of layers) overflow = this.boards[layer].lock(piece) || overflow;
    this.events.push({ type: "lock", piece: { ...piece } });
    const previousLevel = this.level;
    for (const layer of layers) {
      const cleared = this.boards[layer].clearFullLines();
      if (cleared.length === 0) continue;
      this.lines += cleared.length;
      this.score += (LINE_SCORES[cleared.length] ?? 0) * previousLevel;
      this.events.push({ type: "line-clear", layer, rows: cleared });
    }
    this.level = Math.floor(this.lines / GAME.linesPerLevel) + 1;
    if (this.level > previousLevel) this.events.push({ type: "level-up" });
    this.gravityAccumulator = 0;
    this.lockAccumulator = 0;
    this.active = null;
    if (overflow) this.endGame();
    else this.spawn();
  }

  private spawn(): void {
    const type = this.nextType;
    this.nextType = this.bag.next();
    const candidate: ActivePiece = { type, x: Math.floor((GAME.width - 4) / 2), y: 0, rotation: 0, layer: this.foreground };

    if (this.canPlace(candidate)) {
      this.active = candidate;
      return;
    }
    this.active = null;
    this.endGame();
  }

  private endGame(): void {
    this.phase = "game-over";
    this.events.push({ type: "game-over" });
  }

  private canPlace(piece: ActivePiece, x = piece.x, y = piece.y, rotation: Rotation = piece.rotation): boolean {
    const layers: Layer[] = isSpanning(piece.type) ? ["front", "back"] : [piece.layer];
    return layers.every(layer => this.boards[layer].canPlace(piece, x, y, rotation));
  }
}

export function worldCells(piece: ActivePiece): { x: number; y: number }[] {
  return cellsFor(piece.type, piece.rotation).map((cell) => ({
    x: piece.x + cell.x,
    y: piece.y + cell.y,
  }));
}
